import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowCounterClockwise } from "@phosphor-icons/react";

/**
 * Row in the "Live — Assign Referee & Scoring Controls" section of Admin Referees tab.
 * Exposes: referee assignment, scoring_mode (self/referee), scoring_type (live/submission),
 *          and a destructive "Reset" button that clears the match's scoring data.
 */
export default function LiveMatchControlsRow({
  match, players,
  onAssignReferee, onSetScoringMode,
  onSetScoringType, onResetMatch,
}) {
  const p1 = players.find(p => p.id === match.player1_id);
  const p2 = players.find(p => p.id === match.player2_id);
  const refereeCandidates = players.filter(p => p.id !== match.player1_id && p.id !== match.player2_id);

  const effectiveType = match.scoring_type || match.tournament_scoring_type || "submission";
  const isFinalised = ["completed", "walkover", "no_result"].includes(match.status);
  const hasScoreData = (match.player1_score || 0) > 0 || (match.player2_score || 0) > 0
    || !!match.live_started_at || !!match.submitted_at;

  return (
    <div
      className="flex items-start justify-between p-3 rounded-lg bg-muted/20 border border-border gap-3 flex-wrap"
      data-testid={`live-match-row-${match.id}`}
    >
      <div className="flex-1 min-w-[180px]">
        <span className="text-sm font-medium block">{p1?.name} vs {p2?.name}</span>
        <div className="flex items-center gap-1.5 mt-1">
          <Badge variant="outline" className={`text-[9px] capitalize ${
            effectiveType === "live"
              ? "border-destructive/40 text-destructive"
              : "border-primary/30 text-primary"
          }`}>
            {effectiveType === "live" ? "Live" : "Submission"}
          </Badge>
          {hasScoreData && !isFinalised && (
            <Badge variant="outline" className="text-[9px] text-secondary border-secondary/30">
              Scoring started
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select
          value={match.referee_player_id || ""}
          onValueChange={v => onAssignReferee(match.id, v)}
        >
          <SelectTrigger className="w-40 h-8 bg-background border-border text-sm"
            data-testid={`referee-select-${match.id}`}>
            <SelectValue placeholder="Select referee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No referee</SelectItem>
            {refereeCandidates.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={match.scoring_mode || "_default"}
          onValueChange={v => onSetScoringMode(match.id, v)}
        >
          <SelectTrigger className="w-40 h-8 bg-background border-border text-sm"
            data-testid={`scoring-mode-select-${match.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_default">Mode: tournament default</SelectItem>
            <SelectItem value="self">Self-scoring (players)</SelectItem>
            <SelectItem value="referee_only">Referee-only</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={match.scoring_type || "_default"}
          onValueChange={v => onSetScoringType?.(match.id, v)}
        >
          <SelectTrigger className="w-40 h-8 bg-background border-border text-sm"
            data-testid={`scoring-type-select-${match.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_default">Type: tournament default</SelectItem>
            <SelectItem value="submission">Manual Submission</SelectItem>
            <SelectItem value="live">Live Scoring</SelectItem>
          </SelectContent>
        </Select>

        <Button
          size="sm" variant="outline"
          className="h-8 text-xs border-destructive/40 text-destructive hover:bg-destructive/10"
          onClick={() => onResetMatch?.(match.id)}
          disabled={!hasScoreData && !isFinalised}
          data-testid={`reset-match-${match.id}`}
          title={hasScoreData || isFinalised ? "Clear all scoring data on this match" : "Nothing to reset"}
        >
          <ArrowCounterClockwise className="w-3.5 h-3.5 mr-1" />Reset
        </Button>
      </div>
    </div>
  );
}
